<template>
  <div class="block-details">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        variant="text"
        class="mr-2"
        :to="{ name: 'blocks' }"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h1 class="text-h4">Detalhes do Bloco #{{ id }}</h1>
    </div>
    
    <v-skeleton-loader
      v-if="carregando"
      type="card"
      class="mb-4"
    ></v-skeleton-loader>
    <v-skeleton-loader
      v-if="carregando"
      type="table"
    ></v-skeleton-loader>
    
    <template v-else-if="bloco">
      <!-- Painel de Informações do Bloco -->
      <v-card class="mb-4">
        <v-card-title class="d-flex justify-space-between">
          <span>{{ t('blocks.details') }}</span>
          
          <div class="d-flex">
            <v-btn
              icon
              variant="text"
              class="mr-2"
              :disabled="!blocoAnterior"
              :to="blocoAnterior ? { name: 'block-details', params: { id: blocoAnterior } } : ''"
              :aria-label="'Ir para bloco ' + blocoAnterior"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            
            <v-btn
              icon
              variant="text"
              :disabled="!blocoProximo"
              :to="blocoProximo ? { name: 'block-details', params: { id: blocoProximo } } : ''"
              :aria-label="'Ir para bloco ' + blocoProximo"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <InfoItem :label="t('blocks.height')" :valor="bloco.number" />
              <InfoItem :label="t('blocks.hash')" :valor="bloco.hash" copiavel />
              <InfoItem :label="t('blocks.previousHash')" :valor="bloco.parentHash" copiavel />
              <InfoItem label="Estado da Raiz" :valor="bloco.stateRoot" copiavel />
              <InfoItem label="Tempo" :valor="formatarData(bloco.timestamp)" />
            </v-col>
            
            <v-col cols="12" md="6">
              <InfoItem :label="t('blocks.validator')" :valor="bloco.validator" copiavel endereco />
              <InfoItem :label="t('validators.posemScore')" :valor="bloco.posemScore + '/100'" />
              <InfoItem :label="t('tx.title')" :valor="bloco.transactionCount" />
              <InfoItem :label="t('contracts.size')" :valor="formatarBytes(bloco.size)" />
              <InfoItem label="Gas Utilizado" :valor="`${formatarNumeroGrande(bloco.gasUsed)} (${((bloco.gasUsed / bloco.gasLimit) * 100).toFixed(2)}%)`" />
              <InfoItem label="Protocol Fees Coletadas" :valor="formatarProtocolFees(bloco.protocol_fees_collected)" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      
      <!-- Visualização do Consenso -->
      <v-card class="mb-4">
        <v-card-title>
          <v-icon color="primary" class="mr-2">mdi-cube-scan</v-icon>
          Métricas de Consenso
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text>
          <v-row>
            <v-col cols="12" md="3">
              <v-card variant="outlined" class="text-center py-3">
                <div class="text-overline">{{ t('blocks.difficulty') }}</div>
                <div class="text-h6">{{ formatarNumeroGrande(Math.round(bloco.posemScore * 1000)) }}</div>
              </v-card>
            </v-col>
            
            <v-col cols="12" md="3">
              <v-card variant="outlined" class="text-center py-3">
                <div class="text-overline">{{ t('blocks.epochPosition') }}</div>
                <div class="text-h6">{{ bloco.number % 100 }}</div>
              </v-card>
            </v-col>
            
            <v-col cols="12" md="3">
              <v-card variant="outlined" class="text-center py-3">
                <div class="text-overline">{{ t('validators.stake') }}</div>
                <div class="text-h6">{{ formatarValorToken(0) }}</div>
              </v-card>
            </v-col>
            
            <v-col cols="12" md="3">
              <v-card variant="outlined" class="text-center py-3">
                <div class="text-overline">{{ t('validators.merit') }}</div>
                <div class="text-h6">{{ Math.round(bloco.posemScore * 0.8) }}/100</div>
              </v-card>
            </v-col>
          </v-row>
          
          <!-- Gráfico simplificado do consenso -->
          <div class="mt-5 d-flex justify-center align-center" style="height: 150px;">
            <v-progress-circular
              v-for="i in 5"
              :key="i"
              :model-value="0"
              :color="i === 3 ? 'primary' : 'grey'"
              :size="i === 3 ? 80 : 50"
              :width="i === 3 ? 8 : 4"
              class="mx-3"
            >
              <div v-if="i === 3" class="text-caption text-center">
                Bloco<br>Atual
              </div>
            </v-progress-circular>
          </div>
          
          <div class="text-center text-caption mt-2">
            Visualização simplificada do algoritmo PoSEM para o bloco atual.
            <br>
            <v-btn
              variant="text"
              color="primary"
              size="small"
              class="mt-2"
              to="/validators"
            >
              Ver Visualização 3D Completa
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
      
      <!-- Lista de Transações no Bloco -->
      <h2 class="text-h5 mb-3">Transações neste Bloco ({{ (bloco.transactions || []).length }})</h2>
      
      <v-card v-if="(bloco.transactions || []).length > 0">
        <v-data-table
          :headers="cabecalhosTransacoes"
          :items="bloco.transactions || []"
          hover
          density="comfortable"
        >
          <!-- Hash da Transação -->
          <template v-slot:item.hash="{ item }">
            <router-link 
              :to="{ name: 'transaction-details', params: { hash: item.hash } }"
              class="text-primary text-decoration-none"
            >
              <div class="text-truncate" style="max-width: 140px;">
                {{ formatarHash(item.hash) }}
              </div>
            </router-link>
          </template>
          
          <!-- Remetente (From) -->
          <template v-slot:item.from="{ item }">
            <router-link 
              :to="{ name: 'account-details', params: { address: item.from } }"
              class="text-decoration-none"
            >
              <div class="text-truncate" style="max-width: 140px;">
                {{ formatarHash(item.from) }}
              </div>
            </router-link>
          </template>
          
          <!-- Destinatário (To) -->
          <template v-slot:item.to="{ item }">
            <router-link 
              :to="{ name: 'account-details', params: { address: item.to } }"
              class="text-decoration-none"
            >
              <div class="text-truncate" style="max-width: 140px;">
                {{ formatarHash(item.to) }}
              </div>
            </router-link>
          </template>
          
          <!-- Valor -->
          <template v-slot:item.value="{ item }">
            <span class="font-weight-medium">
              {{ formatarValorToken(item.value) }}
            </span>
          </template>
        </v-data-table>
      </v-card>
      
      <v-card v-else class="text-center pa-5">
        <v-icon size="large" color="grey">mdi-package-variant-closed</v-icon>
        <div class="text-h6 grey--text mt-2">{{ t('blocks.empty') }}</div>
        <div class="text-subtitle-2 grey--text">{{ t('blocks.noTx') }}</div>
      </v-card>
    </template>
    
    <v-alert
      v-else
      type="error"
      class="mt-4"
    >
      Bloco #{{ id }} não encontrado ou ocorreu um erro ao carregar os dados.
    </v-alert>
  </div>
</template>

<script>
import { useBlockchainStore } from '@/stores/blockchain'
import { formatarHash, formatarData, formatarBytes, formatarNumeroGrande, formatarValorToken } from '@/services/utilidades'
import InfoItem from '@/components/common/InfoItem.vue'

export default {
  name: 'BlockDetailsView',
  components: {
    InfoItem
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      carregando: true,
      bloco: null,
      cabecalhosTransacoes: [
        { title: 'Hash', key: 'hash', align: 'start' },
        { title: 'De', key: 'from', align: 'start' },
        { title: 'Para', key: 'to', align: 'start' },
        { title: 'Valor', key: 'value', align: 'end' },
        { title: 'Gas', key: 'gas', align: 'end' }
      ]
    }
  },
  computed: {
    blockchainStore() {
      return useBlockchainStore()
    },
    blocoAnterior() {
      const numeroBloco = parseInt(this.id)
      return numeroBloco > 1 ? numeroBloco - 1 : null
    },
    blocoProximo() {
      const numeroBloco = parseInt(this.id)
      const ultimoBloco = this.blockchainStore.getLatestBlockNumber
      
      return numeroBloco < ultimoBloco ? numeroBloco + 1 : null
    }
  },
  methods: {
    formatarHash,
    formatarData,
    formatarBytes,
    formatarNumeroGrande,
    formatarValorToken,

    /**
     * Formata protocol fees de nanoKinX para a moeda da rede.
     * @param {number} nanoKinX - Valor em nanoKinX
     * @returns {string} Valor formatado
     */
    formatarProtocolFees(nanoKinX) {
      const coin = window.__QUARNX_COIN__ || 'tKinX'
      if (nanoKinX === undefined || nanoKinX === null || nanoKinX === 0) {
        return `0 ${coin}`
      }
      const kinx = nanoKinX / 1_000_000_000
      return `${kinx.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 9 })} ${coin}`
    },
    
    async carregarBloco() {
      this.carregando = true
      
      try {
        this.bloco = await this.blockchainStore.fetchBlockById(this.id)
      } catch (error) {
        console.error(`Erro ao carregar bloco #${this.id}:`, error)
        this.bloco = null
      } finally {
        this.carregando = false
      }
    }
  },
  async mounted() {
    await this.carregarBloco()
  },
  watch: {
    id: {
      immediate: true,
      handler() {
        this.carregarBloco()
      }
    }
  }
}
</script>

<style scoped>
.block-details {
  padding-bottom: 32px;
}
</style>
